import { useCallback, useEffect, useRef, useState } from 'react';
import { BELL_JOB_FX_MS, BellJobFx, BellJobFxPlayable, leavesFailureMark, outranksBellJobFx } from './bellJobFx';

export interface IBellJobFxScene {
    fx: BellJobFxPlayable;
    /** Новая сцена — новый id: классы one-shot перезапускаются ремаунтом узлов с этим ключом. */
    id: number;
}

export interface IBellJobFxControls {
    scene: IBellJobFxScene | null;
    /** Есть задача, закончившаяся ошибкой, которую пользователь ещё не видел. */
    failureMark: boolean;
    play: (fx: BellJobFx, jobId: string) => void;
    /** Колокольчик открыли — все ошибки увидены. */
    clearFailureMark: () => void;
    /** Ошибку этой задачи увидели (скрыли её строку): метка остаётся, если есть другие. */
    forgetFailure: (jobId: string) => void;
}

const EMPTY: ReadonlySet<string> = new Set();

/** Сцена колокольчика по итогу фоновой задачи и метка невиданных ошибок; смена пользователя сбрасывает обе. */
export const useBellJobFx = (userId: number | string): IBellJobFxControls => {
    const [scene, setScene] = useState<IBellJobFxScene | null>(null);
    const [failedJobs, setFailedJobs] = useState<ReadonlySet<string>>(EMPTY);
    const sceneRef = useRef<IBellJobFxScene | null>(null);
    const timerRef = useRef<number | null>(null);
    const nextIdRef = useRef(0);
    const userIdRef = useRef(userId);

    const play = useCallback((fx: BellJobFx, jobId: string) => {
        if (fx === 'none' || fx === 'defer') return;
        if (leavesFailureMark(fx)) setFailedJobs((prev) => new Set(prev).add(jobId));
        if (sceneRef.current && outranksBellJobFx(sceneRef.current.fx, fx)) return;

        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        nextIdRef.current += 1;
        const next = { fx, id: nextIdRef.current };
        sceneRef.current = next;
        setScene(next);
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            sceneRef.current = null;
            setScene(null);
        }, BELL_JOB_FX_MS[fx]);
    }, []);

    const clearFailureMark = useCallback(() => setFailedJobs(EMPTY), []);

    const forgetFailure = useCallback(
        (jobId: string) =>
            setFailedJobs((prev) => {
                if (!prev.has(jobId)) return prev;
                const next = new Set(prev);
                next.delete(jobId);
                return next;
            }),
        [],
    );

    useEffect(() => {
        if (userIdRef.current === userId) return;
        userIdRef.current = userId;
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        sceneRef.current = null;
        setScene(null);
        setFailedJobs(EMPTY);
    }, [userId]);

    useEffect(
        () => () => {
            if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        },
        [],
    );

    return { scene, failureMark: failedJobs.size > 0, play, clearFailureMark, forgetFailure };
};
